<template>
    <form class="dropzone vue-dropzone">
        <!-- Not displayed, just for Dropzone's `dictDefaultMessage` option -->
        <div id="dropzone-message" style="display: none">
            <span class="dropzone-title"></span>
            <span class="dropzone-info"></span>
        </div>
        <div ref="previewTemplate" style="display: none;">
            <div class="dz-preview dz-file-preview">
                <div class="status-mark">
                    <div class="dz-success-mark"><span>✔</span></div>
                    <div class="dz-error-mark"><span>✘</span></div>
                </div>
                <div class="dz-details">
                    <div class="dz-filename"><span data-dz-name></span></div>
                    <div class="dz-size" data-dz-size></div>
                    <img data-dz-thumbnail/>
                    <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
                    <div class="dz-error-message"><span data-dz-errormessage></span></div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
    import Dropzone from 'dropzone';
    import AWS from 'aws-sdk';
    import '../../node_modules/dropzone/dist/dropzone.css';
    import {ApiError} from "../errors/ApiError";
    import {ErrorHandler} from "../services/ErrorHandler";
    import {uuid} from "vue-uuid";

    AWS.config.region = 'eu-west-1';
    Dropzone.autoDiscover = false;

    export default {
        name: "FileUploader",
        props: {
            bucket: {type: String},
            dropzoneOptions: {type: Object},
            addFileText: {type: String},
        },
        data() {
            return {
                amountFilesUploaded: 0,
                configExpiration: null,
                valid: true,
            };
        },
        methods: {
            configExpired() {
                return !this.configExpiration || this.configExpiration < Date.now();
            },
            getCredentials() {
                if (this.config) {
                    if (typeof this.config.then === 'function') {
                        return this.config;
                    }

                    if (!this.configExpired()) {
                        return new Promise((resolve) => resolve(this.config));
                    }
                }

                this.config = new Promise((resolve, reject) => {
                    this.$http
                        .post(`${this.$store.getters.api_url}/aws-credentials`)
                        .then(async ({data}) => {
                            if (!data || !data.success) {
                                new ApiError(data);
                                return;
                            }

                            this.configExpiration = data.expiration * 1000;
                            const credentials = data.credentials;

                            AWS.config.credentials = {
                                accessKeyId: credentials.AccessKeyId,
                                secretAccessKey: credentials.SecretAccessKey,
                                sessionToken: credentials.SessionToken,
                            };

                            this.config = AWS.config;
                            resolve(this.config);
                        })
                        .catch(error => reject(error));
                });
                return this.config;
            },
            processQueue(finishedUrl, tags) {
                return new Promise((resolve, reject) => {
                    if (!this.dropzone.files.length) {
                        return resolve();
                    }

                    if (!this.isValid()) {
                        return reject(this.$t('ncrypt.errors.attachment_validation_error_title'));
                    }

                    this.queuedFiles = this.dropzone.getQueuedFiles();

                    this.$on('queueCompleted', () => resolve());

                    this.$on("dropzone-error", (file, message, xhr) => {
                        window.console.log('upload error for file:', file);
                        window.console.log('xhr of upload error:', xhr);
                        reject(message);
                    });

                    this.$on("complete", () => {
                        if (this.amountFilesUploaded === this.queuedFiles.length) {
                            this.$emit("queueCompleted");
                        }
                    });

                    (async () => {
                        const asyncFunctions = this.queuedFiles.map(file => () => this.sendFile(file, tags, finishedUrl));
                        await asyncFunctions.reduce(async (previousPromise, nextAsyncFunction) => {
                            await previousPromise;
                            await nextAsyncFunction();
                        }, Promise.resolve());
                    })();

                });
            },
            sendFile(file, tags, finishedUrl) {
                return new Promise(async (resolve, reject) => {
                    this.dropzone.emit("processing", file);

                    const config = await this.getCredentials();

                    file.s3upload = new AWS.S3.ManagedUpload({
                        config,
                        partSize: 5 * 1024 * 1024,
                        tags: [
                            {Key: 'user-id', Value: this.$store.getters.user.id},
                            ...tags,
                        ],
                        params: {
                            Bucket: this.bucket,
                            Key: `${uuid.v1()}-${file.name}`,
                            Body: file,
                        },
                    });

                    file.s3upload.on('httpUploadProgress', (progress) => {
                        if (progress.total) {
                            const percent = (progress.loaded * 100) / progress.total;
                            this.dropzone.emit('uploadprogress', file, percent, progress.loaded);
                        }
                    });

                    this.dropzone.emit("sending", file);

                    file.s3upload.send((err, data) => {
                        if (err) {
                            this.dropzone.emit("error", file, err.message);
                            reject(err.message);
                            return;
                        }

                        this.storeReferenceAtWebservice(file.name, data.Location, finishedUrl)
                            .then(() => {
                                this.dropzone.emit("success", file);
                                this.dropzone.emit("complete", file);

                                this.amountFilesUploaded++;

                                this.$emit("complete", file);
                                resolve(file);
                            })
                            .catch((reason) => {
                                this.dropzone.emit("error", file, `${this.$t('ncrypt.errors.file_upload_error_saving_on_server')}: ${reason}`);
                                reject(reason);
                            });
                    });
                });
            },
            isValid() {
                const valid = !this.dropzone.getRejectedFiles().length;
                this.valid = valid;
                return valid;
            },
            dropzoneListeners() {
                this.dropzone.on("queuecomplete", () => this.$emit("queueCompleted"));
                // this.dropzone.on("addedfile", (file) => {
                //     setTimeout(() => this.isValid(), 300);
                // });
                this.dropzone.on("error", (file, message, xhr) => this.$emit("dropzone-error", file, message, xhr));
            },
            abortUpload(file) {
                if (file.s3upload) file.s3upload.abort();
            },
            storeReferenceAtWebservice(filename, s3ObjectLocation, finishedUrl) {
                return new Promise((resolve, reject) => {
                    this.$http.post(finishedUrl, {s3ObjectLocation, filename})
                        .then(({data}) => {
                            if (!data || !data.success) {
                                return reject(ErrorHandler.buildApiErrorMessage(data));
                            }
                            resolve();
                        })
                        .catch((error) => reject(error));
                });
            },
        },
        mounted() {
            this.$nextTick(async () => {
                const dropzoneOptions = {
                    url: '/',
                    dictDefaultMessage: `<ion-icon ios="add-circle" md="add-circle"></ion-icon> <span class="add-attachment-text" data-title="${this.addFileText}" data-title-more="${this.$t('ncrypt.attachments.add_more_attachments')}"></span>`,
                    dictCancelUpload: this.$t('ncrypt.cancel'),
                    dictCancelUploadConfirmation: null,
                    dictRemoveFile: '<ion-icon ios="close-sharp" md="close-sharp"></ion-icon>',
                    dictFileTooBig: this.$t('ncrypt.errors.file_upload_file_too_big'),
                    dictInvalidFileType: this.$t('ncrypt.errors.file_upload_file_type_invalid'),
                    dictResponseError: this.$t('ncrypt.errors.file_upload_response_error'),
                    dictUploadCanceled: this.$t('ncrypt.errors.file_upload_cancelled'),
                    dictMaxFilesExceeded: this.$t('ncrypt.errors.file_upload_max_files_exceeded'),
                    previewTemplate: this.$refs.previewTemplate.innerHTML,
                    createImageThumbnails: false,
                    maxFilesize: 30, // MB
                    maxFiles: 4,
                    canceled: this.abortUpload,
                    autoProcessQueue: false,
                    addRemoveLinks: true,
                    parallelUploads: 1,
                    uploadMultiple: false,
                    ...this.dropzoneOptions,
                };

                this.dropzone = new Dropzone(this.$el, dropzoneOptions);

                this.dropzoneListeners();
            });
        },
    }
</script>
<style>
    .dropzone {
        border: none;
        padding: 10px 0;
        float: left;
        width: 100%;
    }

    .dropzone.dz-started {
        padding-bottom: 50px;
    }

    .dropzone.dz-started .dz-message {
        position: absolute;
        margin: 0.5em 0;
        display: initial;
        width: 100%;
        bottom: 0;
        transform: scale(0.8);
    }

    .add-attachment-text {
        margin-left: 3px;
    }

    .add-attachment-text::before {
        content: attr(data-title);
    }
    .dropzone.dz-started .add-attachment-text::before {
        content: attr(data-title-more);
    }

    .dropzone ion-icon {
        border-radius: 50%;
        background: var(--ion-color-primary, #3880ff);
        color: white;
        padding: 0.3rem;
        vertical-align: middle;
        transition: 0.2s linear;
    }

    .dropzone:hover ion-icon {
        background: #3873e6;
    }

    .dropzone .dz-preview {
        width: 100%;
        margin: 0;
        padding: 13px 0;
        min-height: 0;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .dropzone .dz-preview:not(:last-child) {
        border-bottom: 1px solid #eee;
    }

    .dropzone .dz-message {
        margin: 3em 0;
    }

    .dropzone.vue-dropzone .dz-preview .dz-remove {
        opacity: 0;
        align-self: end;
        margin: auto 0 auto auto;
        flex: 0 0 auto;
    }

    .dropzone.vue-dropzone .dz-preview:hover .dz-remove {
        opacity: 1;
    }

    .dropzone.vue-dropzone .dz-remove, .dropzone.vue-dropzone .dz-remove ion-icon {
        cursor: pointer;
    }

    .dropzone.vue-dropzone .dz-preview.dz-success .dz-remove, .dropzone.vue-dropzone .dz-preview.dz-processing .dz-remove {
        display: none;
    }

    .dropzone .dz-preview .dz-progress {
        width: 100%;
        margin: 0;
        position: static;
        -webkit-border-radius: 0;
        -moz-border-radius: 0;
        border-radius: 0;
        background: #eee;
        display: none;
    }

    .dropzone .dz-preview.dz-processing .dz-progress {
        display: block;
    }

    .dropzone .dz-preview.dz-processing {

    }

    .dropzone .dz-preview.dz-complete {

    }

    .dropzone .dz-preview.dz-success {

    }

    .dropzone .dz-preview.dz-error {

    }

    .vue-dropzone .dz-preview .dz-progress .dz-upload {
        background: #3880ff;
    }

    .status-mark {
        display: none;
        width: 30px;
        align-content: center;
        align-items: center;
        flex: 0 0 auto;
    }

    .dropzone.vue-dropzone .dz-preview.dz-success .status-mark, .dropzone.vue-dropzone .dz-preview.dz-error .status-mark {
        display: flex;
    }

    .dropzone.vue-dropzone .dz-preview .dz-success-mark, .dropzone.vue-dropzone .dz-preview .dz-error-mark {
        font-size: 22px;
        opacity: 1;
        display: none;
        position: static;
    }

    .dropzone.vue-dropzone .dz-preview.dz-success .dz-success-mark, .dropzone.vue-dropzone .dz-preview.dz-error .dz-error-mark {
        display: initial;
    }

    .dropzone .dz-preview .dz-success-mark {
        color: #3880ff;
    }

    .dropzone .dz-preview .dz-error-mark {
        color: red;
    }

    .vue-dropzone {
        font-family: "Arial", sans-serif;
        letter-spacing: 0.2px;
        color: #777;
        transition: 0.2s linear;
    }

    .vue-dropzone > i {
        color: #ccc;
    }

    .dropzone.vue-dropzone .dz-preview .dz-details {
        bottom: 0;
        top: 0;
        background-color: transparent;
        transition: opacity 0.2s linear;
        text-align: left;
        position: static;
        padding: 0 10px 0 0;
        display: flex;
        flex-wrap: wrap;
        min-width: 0;
        flex: 1 1 auto;
    }

    .dropzone.vue-dropzone .dz-preview .dz-details > * {
        width: 100%;
    }

    .dropzone .dz-preview .dz-details .dz-filename {
        font-size: 16px;
        overflow: hidden;
        -ms-text-overflow: ellipsis;
        text-overflow: ellipsis;
        border: none;
    }

    .dropzone .dz-preview .dz-details .dz-filename span {
        border: inherit !important;
    }

    .dropzone .dz-preview .dz-details .dz-size {
        font-size: 14px;
        margin-bottom: 3px;
    }

    .dropzone .dz-preview .dz-details .dz-filename span, .dropzone .dz-preview .dz-details .dz-size span {
        background: transparent;
        padding: 0;
    }

    .dropzone.vue-dropzone .dz-preview .dz-details img, .dropzone.vue-dropzone .dz-preview .dz-image {
        display: none;
    }

    .dropzone.vue-dropzone .dz-preview .dz-success-mark,
    .dropzone.vue-dropzone .dz-preview .dz-error-mark {
        margin-left: auto;
        margin-top: auto;
        width: 100%;
        top: 35%;
        left: 0;
    }

    .dropzone.vue-dropzone .dz-preview .dz-success-mark svg,
    .dropzone.vue-dropzone .dz-preview .dz-error-mark svg {
        margin-left: auto;
        margin-right: auto;
    }

    .dropzone.vue-dropzone .dz-preview .dz-error-message {
        margin-left: auto;
        margin-right: auto;
        left: 0;
        width: 100%;
        text-align: center;
    }

    .dropzone.vue-dropzone .dz-preview .dz-error-message {
        position: static;
        width: 100%;
        opacity: 1;
        background: rgba(255, 41, 41, 0.83);
    }

    .dropzone.vue-dropzone .dz-preview .dz-error-message::after {
        display: none;
    }
</style>
